import { clearRegistrationAuthComplete, hasRegistrationAuthSessionFlag, isRegistrationAuthComplete, setRegistrationAuthComplete, type Session } from '@shared/auth';
import { ROUTES } from '@shared/utils';

const REGISTRATION_AUTH_COMPLETE_PARAM = 'registration-auth-complete';
const REGISTRATION_REFERRER_PARAM = 'registration-referrer';
const REGISTRATION_REDIRECT_URL_PARAM = 'redirect_url';

// Internal control params that we add ourselves to the auth return URI. They must never be
// re-copied as "offer/tracking" params nor leaked into the final post-registration redirect.
const INTERNAL_AUTH_RETURN_PARAMS: readonly string[] = [REGISTRATION_AUTH_COMPLETE_PARAM, REGISTRATION_REFERRER_PARAM];

type RegistrationAuthResumeOutcome = 'resume' | 'discard' | 'noop';

function resolveRegistrationAuthReturnUri(baseUrl: string, searchParams: Pick<URLSearchParams, 'entries'>, referrer = '', registrationRedirectUrl: string | null = null): string {
  const url = new URL(ROUTES.REGISTER, baseUrl);

  url.searchParams.set(REGISTRATION_AUTH_COMPLETE_PARAM, 'true');

  // Preserve every incoming query param (offer code, landing page id, tracking params, ...)
  // so no context is lost across the Keycloak OAuth round-trip.
  for (const [key, value] of searchParams.entries()) {
    if (INTERNAL_AUTH_RETURN_PARAMS.includes(key)) {
      continue;
    }
    url.searchParams.set(key, value);
  }

  // The module-level registration context is wiped by the full-page redirect to Keycloak, so
  // carry the original referrer in the URL to keep the legacy "/ob" decision working on return.
  if (referrer) {
    url.searchParams.set(REGISTRATION_REFERRER_PARAM, referrer);
  }

  // redirect_url is stripped from the browser URL after first capture, so re-inject it from context.
  if (registrationRedirectUrl) {
    url.searchParams.set(REGISTRATION_REDIRECT_URL_PARAM, registrationRedirectUrl);
  }

  return url.toString();
}

function getPreservedRegistrationReferrer(searchParams: Pick<URLSearchParams, 'get'>): string | null {
  return searchParams.get(REGISTRATION_REFERRER_PARAM);
}

function isInternalAuthReturnParam(key: string): boolean {
  return INTERNAL_AUTH_RETURN_PARAMS.includes(key);
}

function setRegistrationAuthPending(): void {
  setRegistrationAuthComplete();
}

function shouldResumeRegistrationAfterAuth(): boolean {
  return isRegistrationAuthComplete();
}

function shouldInitialiseRegistrationAuthReturn(searchParams: Pick<URLSearchParams, 'get'>): boolean {
  return hasRegistrationAuthReturnParam(searchParams) && hasRegistrationAuthSessionFlag();
}

function hasRegistrationAuthReturnParam(searchParams: Pick<URLSearchParams, 'get'>): boolean {
  return searchParams.get(REGISTRATION_AUTH_COMPLETE_PARAM) === 'true';
}

function isStaleRegistrationAuthReturnUrl(searchParams: Pick<URLSearchParams, 'get'>): boolean {
  return hasRegistrationAuthReturnParam(searchParams) && !hasRegistrationAuthSessionFlag();
}

function resolveRegistrationAuthResume(input: { isReturnPending: boolean; hadSessionFlag: boolean; isAuthenticated: boolean }): RegistrationAuthResumeOutcome {
  if (!input.isReturnPending) {
    return 'noop';
  }

  if (input.isAuthenticated || input.hadSessionFlag) {
    return 'resume';
  }

  return 'discard';
}

async function settleRegistrationAuthResume(update: () => Promise<Session>): Promise<RegistrationAuthResumeOutcome> {
  const hadSessionFlag = hasRegistrationAuthSessionFlag();
  const isReturnPending = isRegistrationAuthComplete();

  if (!isReturnPending) {
    return 'noop';
  }

  const session = await update();

  return resolveRegistrationAuthResume({
    isReturnPending: true,
    hadSessionFlag,
    isAuthenticated: session.status === 'authenticated',
  });
}

function getRegisterPathWithoutInternalAuthParams(searchParams: Pick<URLSearchParams, 'entries'>): string {
  const params = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (INTERNAL_AUTH_RETURN_PARAMS.includes(key)) {
      continue;
    }
    params.set(key, value);
  }

  const query = params.toString();
  return query ? `${ROUTES.REGISTER}?${query}` : ROUTES.REGISTER;
}

function discardOrphanRegistrationAuthReturn(replace: (href: string) => void, searchParams: Pick<URLSearchParams, 'entries'>): void {
  clearRegistrationAuthComplete();
  replace(getRegisterPathWithoutInternalAuthParams(searchParams));
}

export {
  discardOrphanRegistrationAuthReturn,
  getPreservedRegistrationReferrer,
  getRegisterPathWithoutInternalAuthParams,
  hasRegistrationAuthReturnParam,
  isInternalAuthReturnParam,
  isStaleRegistrationAuthReturnUrl,
  REGISTRATION_AUTH_COMPLETE_PARAM,
  REGISTRATION_REDIRECT_URL_PARAM,
  REGISTRATION_REFERRER_PARAM,
  resolveRegistrationAuthResume,
  resolveRegistrationAuthReturnUri,
  setRegistrationAuthPending,
  settleRegistrationAuthResume,
  shouldInitialiseRegistrationAuthReturn,
  shouldResumeRegistrationAfterAuth,
};
export type { RegistrationAuthResumeOutcome };
